<template>
  <div class="eu-flex eu-column eu-row_md">
    <div class="eu-flex-item eu-basis_4_md">
      <div class="emd-form-group">
        <label
          class="emd-label"
          :class="{ _required: required }"
          for="zipcode"
          >{{ $t("address.zipcode.label") }}</label
        >
        <div class="emd-input-group">
          <the-mask
            class="emd-input"
            type="tel"
            id="zipcode"
            name="zipcode"
            placeholder="22222-222"
            v-model="address.zip_code"
            :mask="'#####-###'"
            :disabled="loadingAddress"
            :class="{
              _state_error:
                $v.address.zip_code.$invalid && showFeedbacksStatusAndAddress,
              _state_warning: cepNotFound,
            }"
            @input="getAddressByZipCode"
          />
          <i
            v-if="loadingAddress"
            class="uil uil-spinner-alt emd-text _color_subtle spin"
          ></i>
        </div>
        <p
          class="emd-input-feedback _state_error"
          v-if="
            $v.address.zip_code.$invalid &&
            showFeedbacksStatusAndAddress &&
            !address.zip_code.length
          "
        >
          {{ $t("hint.required") }}
        </p>
        <p
          class="emd-input-feedback _state_error"
          v-if="
            $v.address.zip_code.$invalid &&
            showFeedbacksStatusAndAddress &&
            address.zip_code.length
          "
        >
          {{ $t("hint.zip_code") }}
        </p>
        <a
          class="emd-input-link"
          href="https://buscacepinter.correios.com.br/app/endereco/index.php"
          rel="noopener"
          target="_blank"
          >{{ $t("address.zipcode.link") }}</a
        >
      </div>
    </div>
    <div class="eu-flex-divider"></div>
    <div class="eu-flex-item eu-basis_12">
      <div class="emd-form-group">
        <label
          class="emd-label"
          :class="{ _required: required }"
          for="street"
          >{{ $t("address.street.label") }}</label
        >
        <input
          class="emd-input"
          type="text"
          name="street"
          id="street"
          v-model="address.street"
          :disabled="loadingAddress"
          :class="{
            _state_error:
              $v.address.street.$invalid && showFeedbacksStatusAndAddress,
          }"
        />
        <p class="emd-input-feedback">{{ $t("address.street.hint") }}</p>
        <p
          class="emd-input-feedback _state_error"
          v-if="!$v.address.street.maxLength && showFeedbacksStatusAndAddress"
        >
          {{ $t("hint.length", { length: "200" }) }}
        </p>
        <p
          class="emd-input-feedback _state_error"
          v-else-if="
            $v.address.street.$invalid && showFeedbacksStatusAndAddress
          "
        >
          {{ $t("hint.required") }}
        </p>
      </div>
    </div>
    <div class="eu-flex-item eu-basis_2_md">
      <div class="emd-form-group">
        <label
          class="emd-label"
          :class="{ _required: required }"
          for="number"
          >{{ $t("address.number.label") }}</label
        >
        <input
          class="emd-input"
          type="text"
          name="number"
          id="number"
          v-model="address.number"
          @input="filterAddressNumber"
          :class="{
            _state_error:
              $v.address.number.$invalid && showFeedbacksStatusAndAddress,
          }"
        />
        <p class="emd-input-feedback">{{ $t("address.number.hint") }}</p>
        <p
          class="emd-input-feedback _state_error"
          v-if="$v.address.number.$invalid && showFeedbacksStatusAndAddress"
        >
          {{ $t("hint.required") }}
        </p>
      </div>
    </div>
    <div class="eu-flex-item eu-basis_6_md">
      <div class="emd-form-group">
        <label class="emd-label" for="complement">{{
          $t("address.complement.label")
        }}</label>
        <input
          class="emd-input"
          name="complement"
          id="complement"
          type="text"
          v-model="address.complement"
          :class="{
            _state_error:
              $v.address.complement.$invalid && showFeedbacksStatusAndAddress,
          }"
        />
        <p class="emd-input-feedback">{{ $t("address.complement.hint") }}</p>
        <p
          class="emd-input-feedback _state_error"
          v-if="$v.address.complement.$invalid && showFeedbacksStatusAndAddress"
        >
          {{ $t("hint.length", { length: "50" }) }}
        </p>
      </div>
    </div>
    <div class="eu-flex-item eu-basis_4_md">
      <div class="emd-form-group">
        <label
          class="emd-label"
          :class="{ _required: required }"
          for="neighborhood"
          >{{ $t("address.neighborhood") }}</label
        >
        <input
          class="emd-input"
          type="text"
          name="neighborhood"
          id="neighborhood"
          v-model="address.neighborhood"
          :disabled="loadingAddress"
          :class="{
            _state_error:
              $v.address.neighborhood.$invalid && showFeedbacksStatusAndAddress,
          }"
        />
        <p
          class="emd-input-feedback _state_error"
          v-if="
            !$v.address.neighborhood.maxLength && showFeedbacksStatusAndAddress
          "
        >
          {{ $t("hint.length", { length: "50" }) }}
        </p>
        <p
          class="emd-input-feedback _state_error"
          v-else-if="
            $v.address.neighborhood.$invalid && showFeedbacksStatusAndAddress
          "
        >
          {{ $t("hint.required") }}
        </p>
      </div>
    </div>
    <div class="eu-flex-item eu-basis_4_md">
      <div class="emd-form-group">
        <label class="emd-label" :class="{ _required: required }" for="city">{{
          $t("address.city")
        }}</label>
        <input
          class="emd-input"
          type="text"
          name="city"
          id="city"
          v-model="address.city"
          :disabled="loadingAddress"
          :class="{
            _state_error:
              $v.address.city.$invalid && showFeedbacksStatusAndAddress,
          }"
        />
        <p
          class="emd-input-feedback _state_error"
          v-if="!$v.address.city.maxLength && showFeedbacksStatusAndAddress"
        >
          {{ $t("hint.length", { length: "64" }) }}
        </p>
        <p
          class="emd-input-feedback _state_error"
          v-else-if="$v.address.city.$invalid && showFeedbacksStatusAndAddress"
        >
          {{ $t("hint.required") }}
        </p>
      </div>
    </div>
    <div class="eu-flex-item eu-basis_2_md">
      <div class="emd-form-group">
        <label class="emd-label" :class="{ _required: required }" for="state">{{
          $t("address.state")
        }}</label>
        <input
          class="emd-input"
          type="text"
          name="state"
          id="state"
          maxlength="2"
          placeholder="UF"
          v-model="address.state"
          @input="toUpperCase"
          :disabled="loadingAddress"
          :class="{
            _state_error:
              $v.address.state.$invalid && showFeedbacksStatusAndAddress,
          }"
        />
        <p
          class="emd-input-feedback _state_error"
          v-if="$v.address.state.$invalid && showFeedbacksStatusAndAddress"
        >
          {{ $t("hint.required") }}
        </p>
      </div>
    </div>
    <div class="eu-flex-item eu-basis_3_md">
      <div class="emd-form-group">
        <label
          class="emd-label"
          :class="{ _required: required }"
          for="country"
          >{{ $t("address.country") }}</label
        >
        <input
          class="emd-input"
          type="text"
          name="country"
          id="country"
          maxlength="2"
          @input="toUpperCase"
          v-model="address.country"
          :disabled="loadingAddress"
          :class="{
            _state_error:
              $v.address.country.$invalid && showFeedbacksStatusAndAddress,
          }"
        />
        <p
          class="emd-input-feedback _state_error"
          v-if="$v.address.country.$invalid && showFeedbacksStatusAndAddress"
        >
          {{ $t("hint.required") }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, numeric, maxLength } from 'vuelidate/lib/validators'
import { isValidZipCode } from '@/utils/validations'

export default {
  name: 'AddressForm',
  data () {
    return {
      loadingAddress: false,
      cepNotFound: false,
      hasAddress: true,
      currentZipCode: null
    }
  },
  props: {
    address: Object,
    required: Boolean
  },
  computed: {
    ...mapGetters(['showFeedbacks', 'tabbarError']),
    formIsInvalid () {
      return this.$v.$invalid
    },
    showAddress () {
      return this.address.show_address
    },
    showFeedbacksStatusAndAddress () {
      return this.showFeedbacks.status && this.showFeedbacks.address
    },
    addressIsLoading () {
      return this.loadingAddress
    }
  },
  validations () {
    return {
      address: {
        city: {
          required,
          maxLength: maxLength(64)
        },
        country: {
          required
        },
        neighborhood: {
          required,
          maxLength: maxLength(50)
        },
        number: {
          required,
          numeric
        },
        state: {
          required
        },
        street: {
          required,
          maxLength: maxLength(200)
        },
        zip_code: {
          required,
          isValidZipCode
        },
        complement: {
          maxLength: maxLength(50)
        }
      }
    }
  },
  methods: {
    getAddressByZipCode () {
      if (
        this.address.zip_code.length === 8 &&
        this.hasAddress &&
        this.address.zip_code !== this.currentZipCode
      ) {
        this.loadingAddress = true
        this.currentZipCode = this.address.zip_code
        this.$store
          .dispatch('SET_ADDRESS', this.address.zip_code)
          .then((res) => {
            this.address.street = res.street
            this.address.neighborhood = res.district
            this.address.state = res.state
            this.address.country = res.country
            this.address.city = res.city
            this.loadingAddress = false
          })
          .catch(() => {
            this.cepNotFound = true
            this.loadingAddress = false
          })
      } else {
        this.cepNotFound = false
      }
      this.hasAddress = true
    },
    toUpperCase () {
      this.address.state = this.address.state
        .replace(/\d+|^\s+$/g, '')
        .toUpperCase()
      this.address.country = this.address.country
        .replace(/\d+|^\s+$/g, '')
        .toUpperCase()
    },
    filterAddressNumber () {
      this.address.number = this.address.number.replace(/[^\d]+/g, '')
    },
    resetAddress () {
      this.address.zip_code = ''
      this.address.city = ''
      this.address.complement = ''
      this.address.country = ''
      this.address.neighborhood = ''
      this.address.number = ''
      this.address.state = ''
      this.address.street = ''
    }
  },
  beforeMount () {
    this.$emit('toggle', this.formIsInvalid)
    if (this.address.street.length || this.required) {
      this.address.show_address = true
      this.hasAddress = false
    } else this.address.show_address = false
  },
  watch: {
    formIsInvalid: {
      handler: function () {
        this.$emit('toggle', this.formIsInvalid)
        this.tabbarError.status = false
        this.showFeedbacks.status = false
      }
    },
    showAddress: {
      handler: function () {
        this.showFeedbacks.address = false
      }
    },
    addressIsLoading: {
      handler: function () {
        this.$emit('addressIsLoading', this.loadingAddress)
      }
    }
  }
}
</script>

<i18n>
  {
    "pt-BR": {
      "address": {
        "title": "Endereço",
        "checkbox": "Adicionar Endereço",
        "zipcode": {
          "label": "CEP",
          "hint": "CEP não encontrado.",
          "link": "Não sei meu CEP"
        },
        "street": {
          "label": "Logradouro",
          "hint": "Digite a rua, avenida ou similar"
        },
        "number": {
          "label": "Número",
          "hint": "Digite somente números."
        },
        "complement": {
          "label": "Complemento",
          "hint": "Digite somente números e/ou letras."
        },
        "neighborhood": "Bairro",
        "city": "Cidade",
        "state": "Estado",
        "country": "País"
      },
      "hint": {
        "required": "Este campo é obrigatório",
        "zip_code": "O CEP deve ter 8 dígitos",
        "length": "Este campo deve conter até {length} caracteres"
      },
      "tabbarError": {
        "address": "Para prosseguir é necessário preencher todos os campos marcados como obrigatórios."
      }
    },
    "en-US": {
      "address": {
        "title": "Address",
        "checkbox": "Add Address",
        "zipcode": {
          "label": "Zip code",
          "hint": "CEP not found.",
          "link": "I do not know my zipcode"
        },
        "street": {
          "label": "Line 1",
          "hint": "Insert a street, avenue or similar"
        },
        "number": {
          "label": "Number",
          "hint": "Insert numbers only."
        },
        "complement": {
          "label": "Line 2",
          "hint": "Insert numbers and/or letters only."
        },
        "neighborhood": "neighborhood",
        "city": "City",
        "state": "State",
        "country": "Country"
      },
      "hint": {
        "required": "This field is required",
        "zip_code": "Zip Code must be a 8-digit number",
        "length": "This field cannot be more than {length} characters"
      },
      "tabbarError": {
        "address": "To move forward it is necessary to fill all fields marked as required."
      }
    }
  }
</i18n>

<style scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"] {
  -moz-appearance: textfield;
}
</style>
